import { when } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IPredicate,
  IPredicateGroup,
  Segment,
  SingularEmailBaseCampaign,
} from '@feathr/blackbox';
import { getSegmentsUniqueEmailsPayload } from '@feathr/blackbox';
import { showError } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

// Segments.get starts a fetch for a group it has not loaded, and a group mid-fetch reads as
// unedited, so wait for every one to land before deriving the payload or the sample predicates.
function whenSegmentsLoaded(segments: Segment[]): Promise<void> {
  return when(() => segments.every((segment) => !segment.isPending));
}

interface IUseEmailAudienceDataParams {
  campaign: SingularEmailBaseCampaign;
  isOpen: boolean;
}

export interface IUseEmailAudienceDataReturn {
  uniqueEmailCount: number | undefined;
  sampleEmails: string[];
  isLoading: boolean;
  error: boolean;
}

/**
 * Fetches audience count and sample email addresses when the modal opens.
 *
 * @param campaign - The email campaign to fetch audience data for.
 * @param isOpen - Whether the modal is currently open. Data is fetched when this transitions to true.
 * @returns Audience count, sample emails, loading state, and error state.
 */
export function useEmailAudienceData({
  campaign,
  isOpen,
}: IUseEmailAudienceDataParams): IUseEmailAudienceDataReturn {
  const { Persons, Segments } = useStore();
  const { t } = useTranslation();
  const [uniqueEmailCount, setUniqueEmailCount] = useState<number | undefined>(undefined);
  const [sampleEmails, setSampleEmails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    async function fetchData(): Promise<void> {
      setIsLoading(true);
      setUniqueEmailCount(undefined);
      setSampleEmails([]);
      setError(false);

      const { excludedSegmentIds, includedSegmentIds } = campaign.audienceSegmentIds;

      const includedSegments = includedSegmentIds.map((id) => Segments.get(id));
      const excludedSegments = excludedSegmentIds.map((id) => Segments.get(id));
      await whenSegmentsLoaded([...includedSegments, ...excludedSegments]);

      const countPromise =
        includedSegmentIds.length > 0
          ? Segments.getUniqueEmailsFromSegments(getSegmentsUniqueEmailsPayload(Segments, campaign))
          : Promise.resolve({ unique_emails: 0 });

      let emails: string[] = [];
      if (includedSegmentIds.length > 0) {
        const firstSegment = includedSegments[0];
        const lookbackValue = firstSegment.get('lookback_value');
        const segmentPredicates = firstSegment.get('predicates', []);
        const segmentMode = firstSegment.get('mode');
        const emailExistsPredicate: IPredicate = {
          kind: 'attribute',
          attr_against: 'email',
          attr_type: 'string',
          comparison: 'exists',
        };

        // AND the segment's predicates with an email-exists filter so sample
        // persons always have a visible email. When the segment uses match_any,
        // wrap its predicates in a group to preserve OR semantics.
        const predicates: Array<IPredicate | IPredicateGroup> = [emailExistsPredicate];
        if (segmentMode === 'match_any' && segmentPredicates.length > 1) {
          predicates.push({
            kind: 'mixed',
            group: segmentPredicates,
            group_mode: 'match_any',
          });
        } else {
          predicates.push(...segmentPredicates);
        }

        // For each excluded segment, add an excluding predicate group so sample
        // persons never include someone filtered out of the real audience.
        for (const excludedSegment of excludedSegments) {
          const excludedPredicates = excludedSegment.get('predicates', []);
          if (excludedPredicates.length === 0) {
            continue;
          }
          predicates.push({
            kind: 'mixed',
            group: excludedPredicates,
            group_mode: excludedSegment.get('mode') ?? 'match_any',
            excludes: true,
          });
        }

        const personsList = Persons.list(
          {
            predicates,
            mode: 'match_all',
            lookback_mode: 'unbounded',
            lookback_value: typeof lookbackValue === 'number' ? lookbackValue : undefined,
            exclude: ['breadcrumbs'],
            pagination: { page_size: 2 },
          },
          { url: Persons.url('page') },
        );

        await when(() => !personsList.isPending);

        emails = personsList.models.map((person) => person.get('email')).filter(Boolean);
      }

      const countResponse = await countPromise;
      if (!cancelled) {
        setUniqueEmailCount(countResponse.unique_emails);
        setSampleEmails(emails);
      }
    }

    fetchData()
      .catch((fetchError: unknown) => {
        if (!cancelled) {
          setUniqueEmailCount(undefined);
          setSampleEmails([]);
          setError(true);
          showError(fetchError, t);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return (): void => {
      cancelled = true;
    };
  }, [isOpen, campaign]);

  return { uniqueEmailCount, sampleEmails, isLoading, error };
}
